import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Stack, Menu, Badge } from '@mui/material';
import { Box, Typography } from 'components/index';
import EditCCCompsSection from './EditCCCompsSection';
import {
  Add,
  ExpandMore,
  Place,
  ViewColumnOutlined as ViewColumnIcon,
  FilterList as FilterListIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { CCReport, CCReportComp } from './construction-cost-report.type';
import { ReportTypes } from 'common/utils/util.type';
import ComparablesMap from '../map/ComparablesMap';
import { CustomManageColumnsMenu } from '../common/CustomManageColumnsMenu';
import { IconOverTextButton } from '../common/IconOverTextButton';
import { ButtonDivider } from '../common/ButtonDivider';
import { GridFilterModel, GridLogicOperator, GridColDef } from '@mui/x-data-grid';
import { getStringOperators, getNumericOperators } from '../../common/utils/datagrid-sieve.util';
import FilterPanel from '../filter-panel/FilterPanel';
import { getAppliedFiltersCount } from '../filter-panel/filter-panel.util';

interface CCComparablesDetailsProps {
  report: CCReport;
  isReportDirty: boolean;
  updateComps: (updatedComps: CCReportComp[]) => void;
  handleOpenCompsDialog: () => void;
  typeOfReport: ReportTypes;
  includeSoftCost: boolean;
  toggleIncludeSoftCost: () => void;
}

export const CCComparablesDetails: React.FC<CCComparablesDetailsProps> = ({
  report,
  isReportDirty,
  updateComps,
  handleOpenCompsDialog,
  typeOfReport,
  includeSoftCost,
  toggleIncludeSoftCost,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const [showMap, setShowMap] = React.useState(false);
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLElement>(null);
  const [tableRef, setTableRef] = React.useState<any>(null);
  const [columnVisibility, setColumnVisibility] = React.useState<Record<string, boolean>>({});
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({ items: [], logicOperator: GridLogicOperator.And });
  const mapsEnabled = process.env.REACT_APP_GOOGLE_MAPS_ENABLED == 'true';

  const handleChange = (isExpanded: boolean) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const showManageColumns = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setColumnsMenuAnchorEl(e.currentTarget);
  };

  const openFilter = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setFilterAnchorEl(e.currentTarget);
  };

  const closeFilter = () => {
    setFilterAnchorEl(null);
  };

  const handleAddCompsClick = (e: any) => {
    e.stopPropagation();
    handleOpenCompsDialog();
  };

  const handleShowMap = (e: any) => {
    e.stopPropagation();
    setShowMap(prevState => !prevState);
  };

  const handleTableReady = (table: any, columnVisibilityState: Record<string, boolean>) => {
    setTableRef(table);
    setColumnVisibility(columnVisibilityState);
  };

  // Define filterable columns for comparables
  const filterColumns: GridColDef[] = [
    {
      field: 'comp.projectTitle',
      headerName: 'Project Title',
      type: 'string',
      filterOperators: getStringOperators(),
    },
    {
      field: 'comp.projectNumber',
      headerName: 'Project Number',
      type: 'string',
      filterOperators: getStringOperators(),
    },
    {
      field: 'comp.projectType',
      headerName: 'Project Type',
      type: 'string',
      filterOperators: getStringOperators(),
    },
    {
      field: 'comp.grossBuildingArea',
      headerName: 'GBA',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'comp.netArea',
      headerName: 'Net Area',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'comp.unitCount',
      headerName: 'Unit Count',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'distance',
      headerName: 'Distance (mi)',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
    {
      field: 'hardCostPerSqFt',
      headerName: 'Hard Cost/SF',
      type: 'number',
      filterOperators: getNumericOperators(),
    },
  ];

  const skippedFields: string[] = [];
  const selectedComps = report.comparableProjects?.filter(comp => comp.isIncluded) || [];

  return (
    <Accordion elevation={2} sx={{ borderRadius: '10px !important' }} expanded={expanded} onChange={handleChange(!expanded)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          '& .MuiAccordionSummary-content': { justifyContent: 'space-between' },
        }}
      >
        <Box display='flex' alignItems='center'>
          <Typography variant='h5' color='primary' sx={{ ml: 1, mb: 0 }}>
            Comparables Details
          </Typography>
        </Box>
        {/* Buttons shown only when accordion is open */}
        {expanded && (
          <Stack direction='row' alignItems='center' spacing={0} sx={{ ml: 'auto', mr: 2 }}>
            <IconOverTextButton
              icon={<AttachMoneyIcon />}
              text='Include Soft Cost'
              tooltip={
                includeSoftCost
                  ? 'Currently including soft cost in calculations. Click to exclude.'
                  : 'Click to include soft cost in calculations'
              }
              onClick={e => {
                e.stopPropagation();
                toggleIncludeSoftCost();
              }}
              sunken={includeSoftCost}
              showHoverEffect={true}
            />

            <IconOverTextButton
              icon={<Place />}
              text={showMap ? 'Hide Map' : 'Show Map'}
              tooltip={showMap ? 'Click to hide map view' : 'Click to show map view'}
              onClick={handleShowMap}
              showHoverEffect={true}
            />

            <IconOverTextButton
              icon={<Add />}
              text='Add Comps'
              tooltip='Add comparable projects'
              onClick={handleAddCompsClick}
              showHoverEffect={true}
            />

            <ButtonDivider />

            <IconOverTextButton
              icon={<ViewColumnIcon />}
              text='Columns'
              tooltip='Manage Columns'
              onClick={showManageColumns}
              showHoverEffect={false}
              wrapInSpan={true}
            />

            <Badge badgeContent={getAppliedFiltersCount(filterModel, skippedFields)} color='primary' overlap='circular'>
              <IconOverTextButton
                icon={<FilterListIcon />}
                text='Filter'
                tooltip='Filter comparables'
                onClick={openFilter}
                showHoverEffect={false}
              />
            </Badge>
          </Stack>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2} sx={{ height: '100%' }}>
          {/* Left column for the EditCCCompsSection */}
          <Grid size={{ xs: showMap ? 6 : 12 }} sx={{ height: '100%' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <EditCCCompsSection
                reportId={report.id}
                isReportDirty={isReportDirty}
                comps={report.comparableProjects || []}
                updateComps={updateComps}
                typeofReport={typeOfReport}
                showMap={showMap}
                includeSoftCost={includeSoftCost}
                onTableReady={handleTableReady}
                onColumnVisibilityChange={setColumnVisibility}
                filterModel={filterModel}
              />
            </Box>
          </Grid>
          {/* Right column for the map, only shown when showMap is true */}
          {showMap && (
            <Grid size={{ xs: 6 }} sx={{ height: '100%' }}>
              <Box height='100%' width='100%'>
                {mapsEnabled ? (
                  <ComparablesMap selectedComps={report?.comparableProjects || []} updateComps={updateComps} isFromCAReport={false} />
                ) : (
                  <p style={{ marginLeft: '20px', color: '#555', fontSize: '16px', fontStyle: 'italic' }}>Maps are disabled.</p>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
      {/* Filter Panel Menu */}
      <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={closeFilter} onClick={e => e.stopPropagation()}>
        <FilterPanel columns={filterColumns} filterModel={filterModel} setFilterModel={setFilterModel} skippedFields={skippedFields} />
      </Menu>
      {/* Custom Manage Columns Menu */}
      {tableRef && (
        <CustomManageColumnsMenu
          anchorEl={columnsMenuAnchorEl}
          setAnchorEl={setColumnsMenuAnchorEl}
          table={tableRef}
          title='Manage Columns'
        />
      )}
    </Accordion>
  );
};

export default CCComparablesDetails;
